import { Box, Spinner } from '@ouster/webviz-react-components';

import { RiBuilding2Line } from 'react-icons/ri';

import DashboardCard from './DashboardCard';
import InfoItem from './InfoItem';
import { InfoItemType, UserOrganization } from './types';
import { formatDate } from './utils';

interface UserLookUpOrganizationProps {
  organizations: UserOrganization[];
  isLoadingUserLookup: boolean;
  isUserFound: boolean;
}

interface OrganizationBoxProps {
  organization: UserOrganization;
}

function OrganizationBox({ organization }: OrganizationBoxProps) {
  const orgInfoItems: InfoItemType[] = [
    { label: 'Organization Name', value: organization.name },
    { label: 'Role', value: organization.role },
    {
      label: 'Status',
      value: organization.status,
      color: organization.status === 'ACTIVE' ? 'success' : 'error',
    },
    { label: 'Organization ID', value: organization.id },
    { label: 'UUID', value: organization.uuid },
    {
      label: 'Created',
      value: formatDate(String(organization.created_at)),
    },
  ];

  return (
    <Box
      border="1px solid"
      borderColor="whiteAlpha.300"
      borderRadius="md"
      p={4}
      mb={4}
      bgColor="whiteAlpha.25"
      data-testid={`user-lookup-org-box-${organization.id}`}
    >
      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap={4}
        rowGap={4}
        data-testid={`user-lookup-org-grid-${organization.id}`}
      >
        {orgInfoItems.map((item) => (
          <InfoItem
            key={item.label}
            label={item.label}
            value={item.value}
            color={item.color}
            data-testid={`user-lookup-org-item-${organization.id}-${item.label
              .toLowerCase()
              .replace(/\s/g, '-')}`}
          />
        ))}
      </Box>
    </Box>
  );
}

export default function UserLookUpOrganization({
  organizations,
  isLoadingUserLookup,
  isUserFound,
}: UserLookUpOrganizationProps) {
  const cardProps = {
    icon: <RiBuilding2Line size={20} />,
    title: `Organizations (${organizations.length})`,
  };

  if (isLoadingUserLookup) {
    return (
      <DashboardCard {...cardProps}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          my={8}
          data-testid="user-lookup-org-loading"
        >
          <Spinner />
        </Box>
      </DashboardCard>
    );
  }

  if (!isUserFound) return null;

  if (!organizations || organizations.length === 0) {
    return (
      <DashboardCard {...cardProps}>
        <Box my={8} data-testid="user-lookup-org-empty">
          User is not associated with any organizations
        </Box>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard {...cardProps} data-testid="user-lookup-org-card">
      <Box data-testid="user-lookup-org-list">
        {organizations.map((org) => (
          <OrganizationBox key={org.id} organization={org} />
        ))}
      </Box>
    </DashboardCard>
  );
}
